/**
 * Boards follow the project scope (cards 3.1 / 3.2). Kept out of BoardsHome so
 * the overview stays one screen of layout:
 *
 *   inProject(board)   the board is in the active scope (All: every board;
 *                      No project: boards no project contains; a project:
 *                      its `contains board:<id>` links)
 *   projectAll         the Personal / Team chips only apply under All projects
 *                      (owner decision 0.4)
 *   heading            "Shop · 3 boards" under a project
 *   menuFor(board)     the tile's ⋯ rows: "Remove from <project>" under that
 *                      project, else "Add to project ›" (the projects the
 *                      board may join and is not in yet)
 */
import React, { useMemo } from 'react';
import { FolderMinus, FolderPlus } from 'lucide-react';
import { ALL_PROJECTS, NO_PROJECT, itemInProject } from '../../scope/projectScopeFilter';
import { useProjectFilterScope } from '../../scope/projectMembership';
import { useProjectScopeStore } from '../../scope/projectScopeStore';
import { addItemToProject, removeItemFromProject } from '../../scope/projectItems';
import { boardNodeId, boardWireScope, projectIdsContaining, projectsForBoard } from './boardProjectScope';

const fmt = (n) => Number(n || 0).toLocaleString();

export default function useBoardsProjectScope() {
  const scope = useProjectFilterScope();
  const projects = useProjectScopeStore((s) => s.known.projects);
  const links = useProjectScopeStore((s) => s.known.links);
  return useMemo(() => {
    const projectAll = scope.activeProjectId === ALL_PROJECTS;
    const active = scope.activeProject;
    const inProject = (board) => itemInProject({ nodeId: boardNodeId(board) }, scope);
    const heading = (count, fallback) => {
      if (projectAll) return fallback;
      const name = scope.activeProjectId === NO_PROJECT ? 'No project' : active?.name || 'Project';
      return `${name} · ${fmt(count)} ${count === 1 ? 'board' : 'boards'}`;
    };
    const warn = (err) => console.warn('[project-scope] board membership failed:', err);
    const menuFor = (board) => {
      const node = boardNodeId(board);
      if (active && scope.memberIds.has(node)) {
        return [{ key: 'project-remove', icon: <FolderMinus size={13} />, label: `Remove from ${active.name}`, onClick: () => removeItemFromProject(active, node).catch(warn) }];
      }
      const already = projectIdsContaining(node, links);
      const targets = projectsForBoard(board, projects).filter((p) => !already.has(String(p.id)));
      if (!targets.length) return [];
      return [{
        key: 'project-add',
        icon: <FolderPlus size={13} />,
        label: 'Add to project',
        items: targets.map((p) => ({ key: `p-${p.id}`, label: p.name, onClick: () => addItemToProject(p, node, boardWireScope(board)).catch(warn) })),
      }];
    };
    return { projectAll, inProject, heading, menuFor, activeName: active?.name || null };
  }, [scope, projects, links]);
}
